import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { theme } from 'twin.macro';
import { GeolocateControl, GeolocateResultEvent, MapRef, ViewStateChangeEvent } from 'react-map-gl';
import debounce from 'lodash.debounce';
import { useRouter } from 'next/router';
import { pickBy } from 'lodash';
import { useAtomValue, useSetAtom } from 'jotai';

import { MapWrapper } from '@restworld/ui-ds';
import { useMapBoundingRadius } from '@restworld/utils-common';

import { clusterLayer } from './map-layers';
import { JobPositionAndRestaurantPins, usePulsingDot } from './job-explorer-map.hooks';
import { useExplorerLoading } from '../job-explorer-content';
import { WorkerHomeLocation } from './worker-home-location';
import { explorerRestaurantListPayloadAtom } from '../job-explorer-restaurant-explorer.atoms';
import {
  getZoomLevelAtom,
  getCenterPointAtom,
  getToggleFullScreenAtom,
  getMapRefAtom,
  setBoundingRadiusAtom,
  setCenterPointAtom,
  setUserCoordinatesAtom,
} from '../job-explorer-map.atoms';
import { hasMapViewAtom, jobListParamsAtom } from '../job-explorer-list.atoms';
import { localityAddressAtom } from '../address-search-bar';
import { CustomMapWrapper, FullScreenCustomControl, MapSpinner } from './job-explorer-map.components';
import { useJobExplorerMap } from './job-explorer-map.hooks/use-explorer-map';

export type ClusterType =
  | GeoJSON.Feature<GeoJSON.Geometry>
  | GeoJSON.FeatureCollection<GeoJSON.Geometry>
  | GeoJSON.Geometry
  | string;

export const useOffset = () => useCallback((index: number) => index * 0.00001, []);

const useInitialViewState = () => {
  const zoomLevel = useAtomValue(getZoomLevelAtom);
  const centerPoint = useAtomValue(getCenterPointAtom);
  return useMemo(() => {
    const [longitude, latitude] = centerPoint;
    return { longitude, latitude, zoom: zoomLevel };
  }, [centerPoint, zoomLevel]);
};

const useInteractiveLayerIds = () => useMemo(() => (clusterLayer.id ? [clusterLayer.id] : []), []);

const useShallowChangeURLOnMove = () => {
  const router = useRouter();
  return useCallback(
    (evt: ViewStateChangeEvent) => {
      router.push(
        {
          query: {
            ...router.query,
            map_filters: JSON.stringify(
              pickBy(evt.viewState, (_, key) => ['longitude', 'latitude', 'zoom'].includes(key))
            ),
          },
        },
        null,
        { shallow: true }
      );
    },
    [router]
  );
};

const useOnMove = ({ mapRef }: { mapRef?: RefObject<MapRef> }) => {
  const { getRadius } = useMapBoundingRadius({ map: mapRef?.current });
  const setBoundingRadius = useSetAtom(setBoundingRadiusAtom);
  const setCenterPoint = useSetAtom(setCenterPointAtom);
  const setExplorerRestaurantListPayload = useSetAtom(explorerRestaurantListPayloadAtom);
  const setJobListParams = useSetAtom(jobListParamsAtom);

  return useCallback(
    (evt: ViewStateChangeEvent) => {
      const radius = getRadius({ lon: evt.viewState.longitude, lat: evt.viewState.latitude });
      setExplorerRestaurantListPayload({
        lon: evt.viewState.longitude,
        lat: evt.viewState.latitude,
        radius,
        offset: 0,
        limit: 3,
      });
      setBoundingRadius(radius);
      setJobListParams((p) => ({
        ...p,
        lon: evt.viewState.longitude,
        lat: evt.viewState.latitude,
        radius,
        offset: 0,
      }));
      setCenterPoint([evt.viewState.longitude, evt.viewState.latitude]);
    },
    [getRadius, setBoundingRadius, setCenterPoint, setExplorerRestaurantListPayload, setJobListParams]
  );
};

const GeoControl = ({ isDesktop }: { isDesktop: boolean }) => {
  const setUserCoordinates = useSetAtom(setUserCoordinatesAtom);
  const onGeolocate = useCallback(
    (v: GeolocateResultEvent) => setUserCoordinates({ lat: v.coords.latitude, lon: v.coords.longitude }),
    [setUserCoordinates]
  );
  return (
    <GeolocateControl
      onGeolocate={onGeolocate}
      showUserLocation={false}
      positionOptions={{ enableHighAccuracy: true }}
      style={{
        margin: !isDesktop ? '16px 16px 0px 0px' : '16px 16px 0 0',
        boxShadow: theme`boxShadow.lg`,
        borderRadius: theme`borderRadius.lg`,
      }}
      trackUserLocation
      position="top-right"
    />
  );
};

export const JobExplorerMap = ({ isDesktop, testId }: { isDesktop: boolean; testId?: string }) => {
  const router = useRouter();
  const [isMapTouch, setIsMapTouch] = useState(false);

  const hasMapView = useAtomValue(hasMapViewAtom);
  const zoomLevel = useAtomValue(getZoomLevelAtom);
  const mapRef = useAtomValue(getMapRefAtom);
  const toggleFullScreen = useAtomValue(getToggleFullScreenAtom);
  const setLocalityAddress = useSetAtom(localityAddressAtom);

  const pulsingEl = usePulsingDot({ mapRef });
  const { handleZoomEndCb, onClick } = useJobExplorerMap();

  const initialViewState = useInitialViewState();
  const interactiveLayerIds = useInteractiveLayerIds();
  const shallowChangeURLOnMove = useShallowChangeURLOnMove();
  const onMove = useOnMove({ mapRef });

  const explorerLoading = useExplorerLoading();

  useEffect(() => {
    mapRef.current?.on('moveend', (evt) => {
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            map_filters: JSON.stringify(
              pickBy(evt.viewState, (_, key) => ['longitude', 'latitude', 'zoom'].includes(key))
            ),
          },
        },
        null,
        { shallow: true }
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef?.current, router]);

  return (
    <CustomMapWrapper data-cy={testId} onMouseEnter={() => setIsMapTouch(true)} toggleFullScreen={toggleFullScreen}>
      <MapWrapper
        cooperativeGestures={false}
        initialViewState={initialViewState}
        interactiveLayerIds={interactiveLayerIds}
        handleZoomEnd={handleZoomEndCb}
        onClick={onClick}
        zoom={zoomLevel}
        onMove={debounce(onMove, 700)}
        mapRef={mapRef}
        onDrag={debounce((evt) => {
          setLocalityAddress({});
          isMapTouch && shallowChangeURLOnMove(evt);
        }, 700)}
        onZoom={debounce((evt) => isMapTouch && shallowChangeURLOnMove(evt), 700)}
      >
        <JobPositionAndRestaurantPins />
        <GeoControl isDesktop={isDesktop} />
        <WorkerHomeLocation />
        {pulsingEl}
        {isDesktop && hasMapView && explorerLoading && <MapSpinner />}
        {isDesktop && <FullScreenCustomControl />}
        {/* {isDesktop && (
          <NavigationControl
            showCompass={false}
            visualizePitch
            style={{ boxShadow: theme`boxShadow.lg`, borderRadius: theme`borderRadius.lg` }}
          />
        )} */}
        {/* <ScaleControl /> */}
        {/* {!isRestaurantsExplorer && <SeasonalRecommendationMapIconFilter />} */}

        {/* <ExplorerTypeToggler /> */}
      </MapWrapper>
    </CustomMapWrapper>
  );
};

export default JobExplorerMap;
